import { useEffect, useRef, useState, type ClipboardEvent, type FormEvent, type KeyboardEvent } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '@services/auth';
import { Button, toast } from '@shared/ui';
import styles from './LoginPage.module.css';

const EMPTY_OTP = ['', '', '', '', '', ''];

/** Inline show/hide-password icon (the shared icon set has no eye glyph). */
function EyeIcon({ off }: { readonly off: boolean }): JSX.Element {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
      {off ? <line x1="3" y1="3" x2="21" y2="21" /> : null}
    </svg>
  );
}

export default function LoginPage(): JSX.Element {
  const { isAuthenticated, signIn, verifyMfa, cancelSignIn, forgotPassword, generateMfaQr, confirmMfaSetup, pendingEmail, mfaSetupPending } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState<readonly string[]>(EMPTY_OTP);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [qr, setQr] = useState<string | null>(null);
  const [setupDone, setSetupDone] = useState(false);
  const otpRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Step is derived from auth state so routing to the enrol screen after signIn()
  // doesn't race a local setStep against the just-updated context.
  const step: 'credentials' | 'mfa' | 'mfa-setup' =
    mfaSetupPending ? 'mfa-setup' : pendingEmail !== null ? 'mfa' : 'credentials';

  // On entering the enrol step, pull the TOTP QR once.
  useEffect(() => {
    if (step !== 'mfa-setup') return;
    let live = true;
    void generateMfaQr().then((img) => { if (live) setQr(img); });
    return () => { live = false; };
  }, [step, generateMfaQr]);

  if (isAuthenticated) return <Navigate to="/dashboard" replace />;

  const focusOtp = (i: number): void => {
    window.setTimeout(() => otpRefs.current[i]?.focus(), 0);
  };

  const onSubmitCredentials = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    const err = await signIn(email, password);
    setBusy(false);
    if (err !== null) { setError(err); return; }
    // Advance is driven by derived `step` (pendingEmail / mfaSetupPending). Focus the
    // first code box for whichever code step we land on.
    focusOtp(0);
  };

  const onConfirmSetup = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    const err = await confirmMfaSetup(otp.join(''));
    setBusy(false);
    if (err !== null) { setError(err); setOtp(EMPTY_OTP); focusOtp(0); return; }
    setSetupDone(true);
  };

  const onForgot = async (): Promise<void> => {
    setError(null);
    if (email.trim() === '') { setError('Enter your email above first, then tap Forgot.'); return; }
    setBusy(true);
    const err = await forgotPassword(email);
    setBusy(false);
    if (err !== null) { setError(err); return; }
    toast.success('Password reset link sent to your email.');
  };

  const setDigit = (i: number, raw: string): void => {
    const digit = raw.replace(/\D/g, '').slice(-1);
    setOtp((prev) => prev.map((d, idx) => (idx === i ? digit : d)));
    if (digit !== '' && i < 5) otpRefs.current[i + 1]?.focus();
  };

  const onOtpKeyDown = (i: number, e: KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Backspace' && (otp[i] ?? '') === '' && i > 0) {
      otpRefs.current[i - 1]?.focus();
    }
  };

  const onOtpPaste = (e: ClipboardEvent<HTMLDivElement>): void => {
    const text = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (text.length === 0) return;
    e.preventDefault();
    setOtp(EMPTY_OTP.map((_, idx) => text[idx] ?? ''));
    focusOtp(Math.min(text.length, 5));
  };

  const onSubmitMfa = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    const err = await verifyMfa(otp.join(''));
    setBusy(false);
    if (err !== null) { setError(err); setOtp(EMPTY_OTP); focusOtp(0); return; }
    navigate('/dashboard', { replace: true });
  };

  const back = (): void => {
    cancelSignIn();
    setOtp(EMPTY_OTP);
    setError(null);
    setQr(null);
    setSetupDone(false);
  };

  const code = otp.join('');

  return (
    <main className={styles.screen}>
      <div className={styles.card}>
        <div className={styles.brand}>
          <img className={styles.logo} src="/pfl-logo.png" alt="PayFacLite" width={132} height={34} />
          <p className={styles.tagline}>Enterprise Operating System for Embedded Commerce</p>
        </div>

        {step === 'credentials' && (
          <form className={styles.form} onSubmit={(e) => { void onSubmitCredentials(e); }} noValidate>
            <h1 className={styles.title}>Sign in</h1>
            <p className={styles.sub}>Access the control plane for your platform.</p>

            <label className={styles.field}>
              <span className={styles.label}>Work email</span>
              <input
                className={styles.input}
                type="email"
                autoComplete="username"
                inputMode="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); }}
                placeholder="you@company.com"
                required
              />
            </label>

            <div className={styles.field}>
              <span className={styles.labelRow}>
                <label htmlFor="pfl-password" className={styles.label}>Password</label>
                <button type="button" className={styles.linkBtn} onClick={() => { void onForgot(); }} disabled={busy}>Forgot?</button>
              </span>
              <div className={styles.inputWrap}>
                <input
                  id="pfl-password"
                  className={styles.input}
                  type={showPw ? 'text' : 'password'}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); }}
                  placeholder="••••••••"
                  required
                />
                <button
                  type="button"
                  className={styles.pwToggle}
                  onClick={() => { setShowPw((v) => !v); }}
                  aria-label={showPw ? 'Hide password' : 'Show password'}
                  aria-pressed={showPw}
                  tabIndex={-1}
                >
                  <EyeIcon off={showPw} />
                </button>
              </div>
            </div>

            {error !== null ? <p className={styles.error} role="alert">{error}</p> : null}

            <Button type="submit" variant="primary" size="lg" className={styles.submit} disabled={busy}>
              {busy ? 'Checking…' : 'Continue'}
            </Button>
          </form>
        )}

        {step === 'mfa' && (
          <form className={styles.form} onSubmit={(e) => { void onSubmitMfa(e); }} noValidate>
            <button type="button" className={styles.backLink} onClick={back}>← Back</button>
            <h1 className={styles.title}>Two-factor authentication</h1>
            <p className={styles.sub}>
              Enter the 6-digit code from your authenticator app for{' '}
              <strong className={styles.email}>{pendingEmail ?? email}</strong>.
            </p>

            <div className={styles.otp} onPaste={onOtpPaste}>
              {otp.map((d, i) => (
                <input
                  key={i}
                  ref={(el) => { otpRefs.current[i] = el; }}
                  className={styles.otpBox}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={1}
                  value={d}
                  onChange={(e) => { setDigit(i, e.target.value); }}
                  onKeyDown={(e) => { onOtpKeyDown(i, e); }}
                  aria-label={`Digit ${String(i + 1)}`}
                />
              ))}
            </div>

            {error !== null ? <p className={styles.error} role="alert">{error}</p> : null}

            <Button type="submit" variant="primary" size="lg" className={styles.submit} disabled={busy || code.length < 6}>
              {busy ? 'Verifying…' : 'Verify & sign in'}
            </Button>

            <p className={styles.hint}>Open your authenticator app to get the current code.</p>
          </form>
        )}

        {step === 'mfa-setup' && !setupDone && (
          <form className={styles.form} onSubmit={(e) => { void onConfirmSetup(e); }} noValidate>
            <button type="button" className={styles.backLink} onClick={back}>← Back</button>
            <h1 className={styles.title}>Set up two-factor authentication</h1>
            <p className={styles.sub}>
              Scan the QR code with your authenticator app, then enter the 6-digit code to finish enrolling.
            </p>

            <div className={styles.qrWrap}>
              {qr !== null
                ? <img className={styles.qr} src={qr} alt="Authenticator QR code" width={180} height={180} />
                : <div className={styles.qrSkeleton} aria-label="Loading QR code" />}
            </div>

            <div className={styles.otp} onPaste={onOtpPaste}>
              {otp.map((d, i) => (
                <input
                  key={i}
                  ref={(el) => { otpRefs.current[i] = el; }}
                  className={styles.otpBox}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={1}
                  value={d}
                  onChange={(e) => { setDigit(i, e.target.value); }}
                  onKeyDown={(e) => { onOtpKeyDown(i, e); }}
                  aria-label={`Digit ${String(i + 1)}`}
                />
              ))}
            </div>

            {error !== null ? <p className={styles.error} role="alert">{error}</p> : null}

            <Button type="submit" variant="primary" size="lg" className={styles.submit} disabled={busy || code.length < 6}>
              {busy ? 'Confirming…' : 'Confirm'}
            </Button>
          </form>
        )}

        {step === 'mfa-setup' && setupDone && (
          <div className={styles.form}>
            <h1 className={styles.title}>You're all set</h1>
            <p className={styles.sub}>
              Two-factor authentication is now active on your account. Sign in again with your authenticator code.
            </p>
            <Button type="button" variant="primary" size="lg" className={styles.submit} onClick={back}>
              Back to sign in
            </Button>
          </div>
        )}
      </div>

      <p className={styles.foot}>Protected by multi-factor authentication · SOC 2 Type II · PCI DSS Level 1</p>
    </main>
  );
}
